import type { InfiniteData, QueryClient } from '@tanstack/react-query';
import type { RealtimeEvent, UserId } from '@cyphex/types';
import type { TimelineResponse } from '@cyphex/types/chat';
import { keepViewerState, reactionsForViewer } from '@/features/chat/lib/viewerState';
import { POST_KEYS } from '@/features/posts/api/queryKeys';
import { appendMessage, patchMessage, removeMessage, type PostCache } from '@/features/posts/lib/cacheUpdates';

type Feed = InfiniteData<TimelineResponse> | undefined;

/**
 * Folds a gateway event into the CHANNEL POST caches.
 *
 * Posts and chat read the same `conversation:<id>` topic but keep separate
 * caches, so both reducers run — writing the cache directly is what makes a new
 * post appear without a refetch, which at 300 people in one channel is the
 * difference between one push and 300 timeline reads.
 */
export function applyEventToPosts(
  client: QueryClient,
  event: RealtimeEvent,
  viewerId: UserId | null,
): void {
  switch (event.type) {
    case 'message.new': {
      const key = event.message.threadRootId
        ? POST_KEYS.thread(event.conversationId, event.message.threadRootId)
        : POST_KEYS.feed(event.conversationId);
      client.setQueryData<Feed>(key, (data) => appendMessage(data, event.message));
      break;
    }
    case 'message.updated':
      /* An edit - or a meeting's card closing itself. The post is replaced;
         what is this READER's on it (star, reminder, their reactions, their
         poll choices) is not in the frame and is kept. */
      client.setQueriesData<PostCache>({ queryKey: POST_KEYS.all }, (data) =>
        patchMessage(data, event.messageId, (mine) => keepViewerState(mine, event.message)),
      );
      break;
    case 'message.deleted':
      if (event.mode === 'hard') {
        client.setQueryData<Feed>(POST_KEYS.feed(event.conversationId), (data) =>
          removeMessage(data, event.messageId),
        );
        break;
      }
      client.setQueriesData<PostCache>({ queryKey: POST_KEYS.all }, (data) =>
        patchMessage(data, event.messageId, (message) => ({
          ...message,
          state: 'deleted',
          deletedAt: new Date().toISOString(),
        })),
      );
      break;
    case 'reaction.added':
    case 'reaction.removed':
      /* The server sends the FULL recomputed pill row — never merge counts.
         WHOSE they are is another matter: the row was read for the person who
         reacted, and taken whole it lit their reactions up as every reader's. */
      client.setQueriesData<PostCache>({ queryKey: POST_KEYS.all }, (data) =>
        patchMessage(data, event.messageId, (message) => ({
          ...message,
          reactions: reactionsForViewer(message.reactions, event, viewerId),
        })),
      );
      break;
    case 'message.pinned':
    case 'message.unpinned': {
      const pinned = event.type === 'message.pinned';
      client.setQueriesData<PostCache>({ queryKey: POST_KEYS.all }, (data) =>
        patchMessage(data, event.messageId, (message) => ({ ...message, isPinned: pinned })),
      );
      void client.invalidateQueries({ queryKey: POST_KEYS.pinned(event.conversationId) });
      break;
    }
    case 'poll.updated':
    case 'poll.closed': {
      /* A meeting poll has no host message; only channel polls patch a card. */
      const hostId = event.messageId;
      if (hostId === null) break;
      client.setQueriesData<PostCache>({ queryKey: POST_KEYS.all }, (data) =>
        patchMessage(data, hostId, (message) => ({
          ...message,
          // The broadcast carries NOBODY's selections — `myVotes` is blanked by
          // the server precisely so one voter's choice is not rendered as
          // everyone's. Keep whatever this client already knows about its own.
          poll: { ...event.poll, myVotes: message.poll?.myVotes ?? event.poll.myVotes },
        })),
      );
      break;
    }
    default:
      break;
  }
}
